import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, SectionCard, Chip } from "../components/Common";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ClipboardCheck, Search, UserPlus, UserCheck, QrCode, Calendar, User, Phone, MapPin, Droplet } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const DISTRICTS = ["Kampala","Wakiso","Mukono","Jinja","Mbarara","Gulu","Mbale","Masaka","Arua","Fort Portal","Lira","Soroti","Kabale","Hoima"];
const DEPARTMENTS = ["General Practice","Paediatrics","Antenatal","Dental","Ophthalmology","ENT","Surgery","Emergency","Maternity"];

const CONSULT_PRESETS = [
  { key: "standard",   label: "Standard consultation",   amount: 15000 },
  { key: "specialist", label: "Specialist consultation", amount: 40000 },
  { key: "followup",   label: "Follow-up consultation",  amount: 5000  },
  { key: "custom",     label: "Custom amount",           amount: 0     },
];

const UGX = (n) => `UGX ${(Number(n) || 0).toLocaleString()}`;

function ConsultationFeeFields({ fee, onChange }) {
  const setPreset = (key) => {
    const preset = CONSULT_PRESETS.find((p) => p.key === key);
    onChange({ preset: key, amount: key === "custom" ? fee.amount : preset.amount });
  };
  const setAmount = (val) => {
    const n = val === "" ? "" : Math.max(0, Number(val) || 0);
    onChange({ preset: "custom", amount: n });
  };
  return (
    <div className="rounded-lg border border-[#e3ebf5] bg-[#f6f9ff] p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#1565C0] font-semibold mb-2 flex items-center gap-1.5">
        <Droplet className="w-3.5 h-3.5" /> Consultation fee
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Fee type</span>
          <select
            data-testid="consult-fee-preset"
            value={fee.preset}
            onChange={(e) => setPreset(e.target.value)}
            className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
          >
            {CONSULT_PRESETS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}{p.key !== "custom" ? ` — ${UGX(p.amount)}` : ""}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Amount (UGX)</span>
          <input
            data-testid="consult-fee-amount"
            type="number"
            min={0}
            step={500}
            value={fee.amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 15000"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
          />
        </label>
      </div>
      <div className="mt-2 text-[12px] text-slate-500">
        This amount is auto-billed as an Unpaid invoice tagged <span className="font-semibold text-slate-700">Consultation</span> the moment the patient joins the queue.
      </div>
    </div>
  );
}

const Input = ({ label, testId, required, ...rest }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}{required && <span className="text-[#D32F2F]"> *</span>}</span>
    <input data-testid={testId} {...rest} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
  </label>
);

const Select = ({ label, testId, options, required, ...rest }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}{required && <span className="text-[#D32F2F]"> *</span>}</span>
    <select data-testid={testId} {...rest} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export default function PatientRegister() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") || ""); // "existing" | "new" | ""
  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <PageHeader title="Patient registration" subtitle="Register a returning patient by Patient ID, or create a new patient record." />

      {!mode && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mode-chooser">
          <button
            data-testid="mode-existing"
            onClick={() => setMode("existing")}
            className="text-left p-6 rounded-xl border border-slate-200 bg-white hover:border-[#1976D2] hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-lg bg-[#e6f0fb] flex items-center justify-center text-[#1565C0] mb-4 group-hover:scale-105 transition-transform"><UserCheck className="w-6 h-6" /></div>
            <div className="font-semibold text-slate-900">Existing patient</div>
            <div className="text-sm text-slate-600 mt-1">The patient already has a VMS Health Card. Look them up by their Patient ID and register today's visit.</div>
            <div className="mt-4 text-[13px] text-[#1565C0] font-medium flex items-center gap-1">Continue <ArrowRight className="w-3.5 h-3.5" /></div>
          </button>
          <button
            data-testid="mode-new"
            onClick={() => setMode("new")}
            className="text-left p-6 rounded-xl border border-slate-200 bg-white hover:border-[#2E7D32] hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-lg bg-[#e6f4ea] flex items-center justify-center text-[#1B5E20] mb-4 group-hover:scale-105 transition-transform"><UserPlus className="w-6 h-6" /></div>
            <div className="font-semibold text-slate-900">New patient</div>
            <div className="text-sm text-slate-600 mt-1">First visit to any VMS facility. We'll capture their details and issue a permanent Patient ID + QR Health Card.</div>
            <div className="mt-4 text-[13px] text-[#2E7D32] font-medium flex items-center gap-1">Continue <ArrowRight className="w-3.5 h-3.5" /></div>
          </button>
        </div>
      )}

      {mode === "existing" && <ExistingPatientFlow onBack={() => setMode("")} onDone={() => navigate("/app/queue")} />}
      {mode === "new" && <NewPatientWizard onBack={() => setMode("")} navigate={navigate} />}
    </div>
  );
}

// ============================================================
// EXISTING PATIENT FLOW — lookup by Patient ID, then add to queue
// ============================================================
function ExistingPatientFlow({ onBack, onDone }) {
  const [thsId, setThsId] = useState("");
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visit, setVisit] = useState({ visit_type: "Outpatient", department: "General Practice", priority: "Normal" });
  const [fee, setFee] = useState({ preset: "standard", amount: 15000 });
  const [queued, setQueued] = useState(null);

  const doLookup = async (e) => {
    e?.preventDefault?.();
    if (!thsId.trim()) { toast.error("Enter a Patient ID"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/lookup?ths_id=${encodeURIComponent(thsId.trim())}`);
      setPatient(data);
      toast.success(`Found: ${data.first_name} ${data.last_name}`);
    } catch (err) {
      const msg = err?.response?.status === 404 ? "No patient found with that Patient ID at this facility" : "Lookup failed";
      toast.error(msg);
      setPatient(null);
    } finally { setLoading(false); }
  };

  const registerVisit = async () => {
    if (!patient) return;
    const amt = Number(fee.amount) || 0;
    if (amt <= 0) {
      const ok = window.confirm("No consultation fee entered — continue without billing?");
      if (!ok) return;
    }
    try {
      const { data } = await api.post("/queue", {
        patient_id: patient.patient_id,
        ...visit,
        consultation_fee: amt,
        consultation_type: fee.preset,
      });
      setQueued(data);
      if (data.invoice?.invoice_no) {
        toast.success(`${patient.first_name} added to queue · ${UGX(data.invoice.total)} billed on ${data.invoice.invoice_no}`);
      } else {
        toast.success(`${patient.first_name} added to today's queue`);
      }
    } catch { toast.error("Could not add to queue"); }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="existing-flow">
      <div className="flex items-center gap-2">
        <button onClick={onBack} data-testid="existing-back" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Change mode</button>
      </div>

      <SectionCard title="Look up existing patient">
        <form onSubmit={doLookup} className="p-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="existing-ths-input"
              value={thsId}
              onChange={(e) => setThsId(e.target.value.toUpperCase())}
              placeholder="e.g. SM 2401-8342"
              className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
              autoFocus
            />
          </div>
          <button data-testid="existing-lookup-btn" disabled={loading} type="submit" className="px-5 h-11 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold disabled:opacity-60">{loading ? "Searching…" : "Look up patient"}</button>
        </form>
        <div className="px-5 pb-5 -mt-2 text-[12px] text-slate-500">You can also paste the ID without spaces (e.g. <span className="tabular">SM24018342</span>).</div>
      </SectionCard>

      {patient && (
        <SectionCard title="Patient identified" testId="existing-patient-card">
          <div className="p-5 flex items-start gap-5 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xl font-semibold">
              {(patient.first_name?.[0] || "")}{(patient.last_name?.[0] || "")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-lg font-semibold text-slate-900">{patient.first_name} {patient.middle_name} {patient.last_name}</div>
                <Chip tone="blue">{patient.ths_id}</Chip>
                <Chip tone="slate">{patient.gender}</Chip>
                {patient.blood_group && patient.blood_group !== "Unknown" && <Chip tone="red">Blood {patient.blood_group}</Chip>}
                {(patient.allergies || []).length > 0 && <Chip tone="amber">Allergies: {(patient.allergies || []).join(", ")}</Chip>}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> DOB {patient.date_of_birth || "—"}</span>
                <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {patient.phone_primary}</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {patient.district || "—"}</span>
                <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {patient.insurance_provider || "None"}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Register today's visit</div>
            {queued ? (
              <div className="space-y-3" data-testid="existing-queued">
                <div className="rounded-lg bg-[#eef8f0] border border-[#c9e5d1] px-4 py-3 text-sm text-[#1B5E20] font-medium flex items-start gap-2">
                  <UserCheck className="w-4 h-4 mt-0.5" />
                  <div>
                    {patient.first_name} added to the {visit.department} queue{queued.queue_no ? ` at position #${queued.queue_no}` : ""}.
                    {queued.invoice?.invoice_no && (
                      <div className="mt-1 text-[#0b3a15]">Consultation fee of <span className="tabular font-semibold">{UGX(queued.invoice.total)}</span> billed — invoice <span className="tabular font-semibold" data-testid="existing-invoice-no">{queued.invoice.invoice_no}</span>.</div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="existing-goto-queue" onClick={() => onDone?.()} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Go to queue</button>
                  <button data-testid="existing-open-chart-post" onClick={() => window.location.assign(`/app/patients/${patient.patient_id}`)} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Open chart</button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Select testId="visit-type" label="Visit type" options={["Outpatient","Emergency","Follow-up","Antenatal","Immunization","Laboratory only","Pharmacy only"]} value={visit.visit_type} onChange={(e) => setVisit((v) => ({ ...v, visit_type: e.target.value }))} />
                  <Select testId="visit-dept" label="Department" options={DEPARTMENTS} value={visit.department} onChange={(e) => setVisit((v) => ({ ...v, department: e.target.value }))} />
                  <Select testId="visit-priority" label="Priority" options={["Normal","Urgent","Emergency"]} value={visit.priority} onChange={(e) => setVisit((v) => ({ ...v, priority: e.target.value }))} />
                </div>
                <div className="mt-4"><ConsultationFeeFields fee={fee} onChange={setFee} /></div>
                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button data-testid="existing-open-chart" onClick={() => window.location.assign(`/app/patients/${patient.patient_id}`)} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Open chart</button>
                  <button data-testid="existing-register-visit" onClick={registerVisit} className="px-5 h-10 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold flex items-center gap-1.5"><UserCheck className="w-4 h-4" /> Register visit & add to queue</button>
                </div>
              </>
            )}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ============================================================
// NEW PATIENT — 4-step wizard (as before, extracted into a component)
// ============================================================
function NewPatientWizard({ onBack, navigate }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [visit, setVisit] = useState({ visit_type: "Outpatient", department: "General Practice", priority: "Normal" });
  const [fee, setFee] = useState({ preset: "standard", amount: 15000 });
  const [queuedResult, setQueuedResult] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", middle_name: "", gender: "Female", date_of_birth: "",
    national_id: "", nationality: "Ugandan", marital_status: "Single",
    phone_primary: "", phone_secondary: "", email: "",
    emergency_name: "", emergency_relation: "", emergency_phone: "",
    village: "", parish: "", subcounty: "", district: "Kampala", country: "Uganda",
    next_of_kin: "", kin_relation: "", kin_phone: "", occupation: "",
    blood_group: "Unknown", allergies: "",
    insurance_provider: "None", insurance_policy: "", insurance_member: "", insurance_expiry: "",
    preferred_payment: "Cash", patient_category: "General",
    consent_ehr: false, consent_sms: false, consent_email: false, consent_share: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (step === 1 && (!form.first_name || !form.last_name || !form.gender)) { toast.error("Name and gender are required"); return false; }
    if (step === 2 && !form.phone_primary) { toast.error("Primary phone is required"); return false; }
    if (step === 4 && !form.consent_ehr) { toast.error("Patient must consent to create the EHR"); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, allergies: form.allergies ? form.allergies.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      const { data } = await api.post("/patients", payload);
      setCreated(data);
      toast.success(`Patient registered: ${data.ths_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally { setSaving(false); }
  };

  const registerVisitAndQueue = async () => {
    if (!created) return;
    const amt = Number(fee.amount) || 0;
    if (amt <= 0) {
      const ok = window.confirm("No consultation fee entered — continue without billing?");
      if (!ok) return;
    }
    try {
      const { data } = await api.post("/queue", {
        patient_id: created.patient_id,
        ...visit,
        consultation_fee: amt,
        consultation_type: fee.preset,
      });
      setQueuedResult(data);
      if (data.invoice?.invoice_no) {
        toast.success(`${created.first_name} added to queue · ${UGX(data.invoice.total)} billed on ${data.invoice.invoice_no}`);
      } else {
        toast.success(`${created.first_name} added to today's queue`);
      }
    } catch { toast.error("Could not add to queue"); }
  };

  if (created) {
    return (
      <div className="mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="print-card bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="qr-card-front">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md overflow-hidden ring-1 ring-slate-200"><img src="/vms-logo.png" alt="VMS" className="w-full h-full object-cover" /></div>
                <div className="font-semibold text-slate-900 text-sm">Vitality Medical Software</div>
              </div>
              <div className="text-[10px] text-slate-500">HEALTH CARD</div>
            </div>
            <div className="mt-6 flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-2xl font-semibold text-slate-500">
                {(created.first_name?.[0] || "")}{(created.last_name?.[0] || "")}
              </div>
              <div>
                <div className="text-lg font-bold tracking-tight">{created.first_name} {created.middle_name} {created.last_name}</div>
                <div className="text-xs text-slate-500 mt-1">DOB: {created.date_of_birth || "—"} · {created.gender}</div>
                <div className="text-xs text-slate-500">Blood: {created.blood_group || "Unknown"}</div>
              </div>
            </div>
            <div className="mt-6 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Patient ID</div>
                <div className="tabular text-2xl font-bold text-[#1565C0]">{created.ths_id}</div>
              </div>
              <div className="text-[10px] text-slate-500">Issued {new Date().toLocaleDateString()}</div>
            </div>
          </div>
          <div className="print-card bg-[#0a1628] text-white rounded-2xl p-6" data-testid="qr-card-back">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Scan at VMS-enabled facility</div>
              <div className="text-[10px] text-slate-400">vitalitymedicalsoftware.com</div>
            </div>
            <div className="mt-4 flex items-center justify-center bg-white rounded-xl p-5">
              <QRCodeSVG value={`THS://${created.ths_id.replace(/\s/g,'')}|${created.patient_id}`} size={180} />
            </div>
            <div className="mt-4 text-[11px] text-slate-300 leading-relaxed">
              This code links only to a signed identifier. Scanning requires authenticated access and is recorded in the audit log. Emergency contact: {created.emergency_phone || created.phone_primary || "—"}
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-2 print-hidden">
          <button data-testid="print-card-btn" onClick={() => window.print()} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Print card</button>
          <button data-testid="new-register-btn" onClick={() => { setCreated(null); setQueuedResult(null); setStep(1); setForm({ ...form, first_name: "", last_name: "", middle_name: "", phone_primary: "", national_id: "", consent_ehr: false }); }} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Register another</button>
          <button data-testid="open-chart-btn" onClick={() => navigate(`/app/patients/${created.patient_id}`)} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Open patient chart</button>
        </div>

        {/* Register today's visit — same panel we show for returning patients */}
        <div className="mt-6">
          <SectionCard title={queuedResult ? "Patient added to queue" : "Register today's visit"} testId="new-patient-visit-card">
            <div className="p-5 flex items-start gap-4 flex-wrap border-b border-slate-200">
              <div className="w-14 h-14 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-lg font-semibold flex-shrink-0">
                {(created.first_name?.[0] || "")}{(created.last_name?.[0] || "")}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-base font-semibold text-slate-900">{created.first_name} {created.middle_name} {created.last_name}</div>
                  <Chip tone="blue">{created.ths_id}</Chip>
                  <Chip tone="slate">{created.gender}</Chip>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> DOB {created.date_of_birth || "—"}</span>
                  <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {created.phone_primary || "—"}</span>
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {created.district || "—"}</span>
                  <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {created.insurance_provider || "None"}</span>
                </div>
              </div>
            </div>

            {queuedResult ? (
              <div className="p-5 flex flex-col items-start gap-3">
                <div className="rounded-lg bg-[#eef8f0] border border-[#c9e5d1] px-4 py-3 text-sm text-[#1B5E20] font-medium flex items-start gap-2">
                  <UserCheck className="w-4 h-4 mt-0.5" />
                  <div>
                    {created.first_name} is in the {visit.department} queue{typeof queuedResult.queue_no === "number" ? ` at position #${queuedResult.queue_no}` : ""}. A doctor will see them shortly.
                    {queuedResult.invoice?.invoice_no && (
                      <div className="mt-1 text-[#0b3a15]">Consultation fee of <span className="tabular font-semibold">{UGX(queuedResult.invoice.total)}</span> billed — invoice <span className="tabular font-semibold" data-testid="new-invoice-no">{queuedResult.invoice.invoice_no}</span>.</div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid="new-goto-queue" onClick={() => navigate("/app/queue")} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Go to queue</button>
                  <button data-testid="new-register-another-2" onClick={() => { setCreated(null); setQueuedResult(null); setStep(1); setForm({ ...form, first_name: "", last_name: "", middle_name: "", phone_primary: "", national_id: "", consent_ehr: false }); }} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Register another patient</button>
                </div>
              </div>
            ) : (
              <div className="p-5">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Register today's visit</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Select testId="new-visit-type" label="Visit type" options={["Outpatient","Emergency","Follow-up","Antenatal","Immunization","Laboratory only","Pharmacy only"]} value={visit.visit_type} onChange={(e) => setVisit((v) => ({ ...v, visit_type: e.target.value }))} />
                  <Select testId="new-visit-dept" label="Department" options={DEPARTMENTS} value={visit.department} onChange={(e) => setVisit((v) => ({ ...v, department: e.target.value }))} />
                  <Select testId="new-visit-priority" label="Priority" options={["Normal","Urgent","Emergency"]} value={visit.priority} onChange={(e) => setVisit((v) => ({ ...v, priority: e.target.value }))} />
                </div>
                <div className="mt-4"><ConsultationFeeFields fee={fee} onChange={setFee} /></div>
                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button data-testid="new-open-chart" onClick={() => navigate(`/app/patients/${created.patient_id}`)} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Open chart</button>
                  <button data-testid="new-register-visit" onClick={registerVisitAndQueue} className="px-5 h-10 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold flex items-center gap-1.5"><UserCheck className="w-4 h-4" /> Register visit & add to queue</button>
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    );
  }

  const steps = [
    { n: 1, label: "Identity" }, { n: 2, label: "Contact" },
    { n: 3, label: "Medical & Payment" }, { n: 4, label: "Consent" },
  ];

  return (
    <div className="mt-6 space-y-6" data-testid="new-flow">
      <div className="flex items-center gap-2">
        <button onClick={onBack} data-testid="new-back" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Change mode</button>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="wizard-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 ${step >= s.n ? "text-[#1565C0]" : "text-slate-400"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold ${step > s.n ? "bg-[#2E7D32] text-white" : step === s.n ? "bg-[#1976D2] text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
              </div>
              <div className="text-sm font-medium">{s.label}</div>
            </div>
            {i < steps.length - 1 && <div className="h-px bg-slate-200 flex-1 min-w-4" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input testId="reg-first-name" label="First name" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
            <Input testId="reg-middle-name" label="Middle name" value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} />
            <Input testId="reg-last-name" label="Last name" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
            <Input testId="reg-dob" type="date" label="Date of birth" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
            <Select testId="reg-gender" label="Gender" required options={["Female","Male","Other"]} value={form.gender} onChange={(e) => set("gender", e.target.value)} />
            <Select testId="reg-marital" label="Marital status" options={["Single","Married","Divorced","Widowed"]} value={form.marital_status} onChange={(e) => set("marital_status", e.target.value)} />
            <Input testId="reg-national-id" label="National ID (NIN)" value={form.national_id} onChange={(e) => set("national_id", e.target.value)} placeholder="CM..." />
            <Input testId="reg-nationality" label="Nationality" value={form.nationality} onChange={(e) => set("nationality", e.target.value)} />
          </div>
        )}
        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input testId="reg-phone" label="Primary phone" required value={form.phone_primary} onChange={(e) => set("phone_primary", e.target.value)} placeholder="+256 7XX XXX XXX" />
            <Input testId="reg-phone2" label="Secondary phone" value={form.phone_secondary} onChange={(e) => set("phone_secondary", e.target.value)} />
            <Input testId="reg-email" type="email" label="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            <Input testId="reg-occupation" label="Occupation" value={form.occupation} onChange={(e) => set("occupation", e.target.value)} />
            <Input testId="reg-village" label="Village" value={form.village} onChange={(e) => set("village", e.target.value)} />
            <Input testId="reg-parish" label="Parish" value={form.parish} onChange={(e) => set("parish", e.target.value)} />
            <Input testId="reg-subcounty" label="Sub-county" value={form.subcounty} onChange={(e) => set("subcounty", e.target.value)} />
            <Select testId="reg-district" label="District" options={DISTRICTS} value={form.district} onChange={(e) => set("district", e.target.value)} />
            <Input testId="reg-emergency-name" label="Emergency contact name" value={form.emergency_name} onChange={(e) => set("emergency_name", e.target.value)} />
            <Input testId="reg-emergency-phone" label="Emergency contact phone" value={form.emergency_phone} onChange={(e) => set("emergency_phone", e.target.value)} placeholder="+256..." />
            <Input testId="reg-kin-name" label="Next of kin" value={form.next_of_kin} onChange={(e) => set("next_of_kin", e.target.value)} />
            <Input testId="reg-kin-phone" label="Next of kin phone" value={form.kin_phone} onChange={(e) => set("kin_phone", e.target.value)} />
          </div>
        )}
        {step === 3 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select testId="reg-blood" label="Blood group" options={["Unknown","O+","O-","A+","A-","B+","B-","AB+","AB-"]} value={form.blood_group} onChange={(e) => set("blood_group", e.target.value)} />
            <Input testId="reg-allergies" label="Allergies (comma-separated)" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="Penicillin, Sulfa" />
            <Select testId="reg-insurance" label="Insurance provider" options={["None","UAP Old Mutual","Prudential","AAR","Sanlam","IAA","Jubilee"]} value={form.insurance_provider} onChange={(e) => set("insurance_provider", e.target.value)} />
            <Input testId="reg-insurance-member" label="Insurance member number" value={form.insurance_member} onChange={(e) => set("insurance_member", e.target.value)} />
            <Select testId="reg-payment" label="Preferred payment" options={["Cash","MTN Mobile Money","Airtel Money","Card","Insurance"]} value={form.preferred_payment} onChange={(e) => set("preferred_payment", e.target.value)} />
            <Select testId="reg-category" label="Patient category" options={["General","VIP","Corporate","Student","Pediatric","Maternity"]} value={form.patient_category} onChange={(e) => set("patient_category", e.target.value)} />
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3">
            {[
              ["consent_ehr","Consent to create the electronic health record (required)"],
              ["consent_sms","Consent to receive SMS notifications from VMS"],
              ["consent_email","Consent to receive email notifications from VMS"],
              ["consent_share","Consent to share records with authorized providers"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg">
                <input data-testid={`reg-${k}`} type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1 accent-[#1976D2]" />
                <div className="text-sm text-slate-700">{label}</div>
              </label>
            ))}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-[13px] text-slate-600 flex items-start gap-2">
              <ClipboardCheck className="w-4 h-4 text-[#1565C0] mt-0.5" />
              <div>Patient information is stored securely and accessible only to authorized staff. Every access is recorded in the audit log.</div>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <button
            data-testid="wizard-back"
            disabled={step === 1}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5 disabled:opacity-40"
          ><ArrowLeft className="w-4 h-4" /> Back</button>

          {step < 4 && (
            <button
              data-testid="wizard-next"
              onClick={() => { if (validate()) setStep((s) => Math.min(4, s + 1)); }}
              className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"
            >Continue <ArrowRight className="w-4 h-4" /></button>
          )}
          {step === 4 && (
            <button
              data-testid="wizard-submit"
              disabled={saving}
              onClick={submit}
              className="px-5 h-10 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60"
            >{saving ? "Registering…" : "Register patient"}</button>
          )}
        </div>
      </div>
    </div>
  );
}
